// @flow

import React, { type Node } from 'react';
import styled from 'styled-components';
import { getReceiptAmountLocalTotal } from '@tectonics/prs/receipts/receipt';
import { useIncomingPayment, getFinalReceipt, getCountry } from '@tectonics/prs/incoming-payment';
import { amountToCurrencyString } from '@tectonics/prs/money';
import { getMoneyFormat } from '../../../../commons/money-format';

export function Header(): Node {
  const incomingPayment = useIncomingPayment();
  const country = getCountry(incomingPayment);

  switch (country) {
    case 'br':
      return <BrazilianHeader />;

    case 'ar':
    case 'bo':
    case 'cl':
    case 'co':
    case 'ec':
    case 'mx':
    case 'pe':
    case 'uy':
    case 'py':
    case 'pa':
    case 'cr':
    case 'sv':
    case 'gt':
    case 'do':
      return <SpanishHeader />;

    case 'eg':
      return <ArabicHeader />;

    case 'za':
    case 'ke':
    case 'ng':
    case 'in':
    case 'tz':
    case 'ug':
    case 'zm':
    case 'jm':
      return <EnglishHeader />;

    default:
      throw new Error(`CO Payment Header: country ${country} is not supported`);
  }
}

function BrazilianHeader(): Node {
  const incomingPayment = useIncomingPayment();
  const receipt = getFinalReceipt(incomingPayment);
  const decimalFormat = getMoneyFormat(incomingPayment.country);
  const formatAmount = amountToCurrencyString(decimalFormat);

  return (
    <Root>
      Seu pagamento
      de <Amount> {formatAmount(getReceiptAmountLocalTotal(receipt))} </Amount> foi
      aprovado \o/
    </Root>
  );
}

function SpanishHeader(): Node {
  const incomingPayment = useIncomingPayment();
  const receipt = getFinalReceipt(incomingPayment);
  const decimalFormat = getMoneyFormat(incomingPayment.country);
  const formatAmount = amountToCurrencyString(decimalFormat);

  return (
    <Root>
      Su pago
      de <Amount> {formatAmount(getReceiptAmountLocalTotal(receipt))} </Amount> fue
      aprobado \o/
    </Root>
  );
}

function EnglishHeader(): Node {
  const incomingPayment = useIncomingPayment();
  const receipt = getFinalReceipt(incomingPayment);
  const decimalFormat = getMoneyFormat(incomingPayment.country);
  const formatAmount = amountToCurrencyString(decimalFormat);

  return (
    <Root>
      Your <Amount> {formatAmount(getReceiptAmountLocalTotal(receipt))} </Amount> payment
      has been
      approved \o/
    </Root>
  );
}

export function ArabicHeader(): Node {
  const incomingPayment = useIncomingPayment();
  const receipt = getFinalReceipt(incomingPayment);
  const decimalFormat = getMoneyFormat(incomingPayment.country);
  const formatAmount = amountToCurrencyString(decimalFormat);

  return (
    <Root>
      تمت الموافقة على عملية الدفع الخاصة بك بقيمة <Amount> {formatAmount(getReceiptAmountLocalTotal(receipt))} </Amount> جنيه مصري.
    </Root>
  );
}

const Root = styled.h1`
  font-family: ${props => props.theme.fontFamily.display};
  font-weight: 200;
  font-size: 1.4em;
  line-height: 1.5;
  color: ${props => props.theme.colors.primary(39)};
  background-color: ${props => props.theme.colors.gray(96)};
  padding: 16px 32px;

  @media (min-width: 840px) {
    font-size: 2em;
    color: inherit;
    background-color: transparent;
    padding: 32px;
  }
`;

const Amount = styled.span`
  display: inline-block;
`;
